import { parseBraceOrCsv } from '../core/EncodeValues.js';
import { Loc } from '../locales/registry.js';

// search-core/FilterChipsManager.ts
/**
 * Менеджер DOM для чипсов фильтра.
 *
 * Отвечает только за:
 * - normalize(): склейка значений по базовому ключу (учёт __till)
 * - render(): синхронизация состояния + отрисовка DOM
 *
 * Обработчиков кликов здесь НЕТ — клики делегируются снаружи.
 *
 * Поддержка групп:
 * - внутри host можно иметь несколько контейнеров `.chips__group[data-group="..."]`
 * - у группы может быть заголовок (в разметке или через data-title), который скрывается при пустых чипсах
 * - кнопка `bt-trash` ("clear all") может быть в конце (default) или в начале (data-deletepos="start")
 */
class FilterChipsManager {
    static getOrInit(host) {
        let inst = this.instances.get(host);
        if (!inst) {
            inst = new FilterChipsManager(host);
            this.instances.set(host, inst);
        }
        return inst;
    }
    constructor(host) {
        /** Храним нормализованные чипсы в порядке добавления */
        this.chips = [];
        /** Порядок групп (groupKey) в DOM */
        this.groupOrder = [];
        this.host = host;
    }
    /** Нормализовать "сырые" значения в список чипсов (близко к legacy, только __till + arrays) */
    normalize(entries, sourceKey = "default") {
        // groupKey -> (baseKey -> Agg)
        const groups = new Map();
        for (const e of entries) {
            const schema = e.schema;
            const v = e.value;
            // 1) скрытые в чипсах поля пропускаем
            if (schema.showInChips === false)
                continue;
            if (!v)
                continue;
            // 2) спец-ключи (архивная дата)
            if (v.key.endsWith("__type"))
                continue;
            const valueOnly = schema.chipsValueOnly === true;
            const groupKey = (schema.chipsGroup ?? "default").trim() || "default";
            const { baseKey, part } = this.getBaseKey(v.key);
            let map = groups.get(groupKey);
            if (!map) {
                map = new Map();
                groups.set(groupKey, map);
            }
            const entry = map.get(baseKey) ??
                {
                    key: baseKey,
                    label: schema.label || v.label,
                    starts: [],
                    tills: [],
                    singles: [],
                    groupKey,
                    valueOnly
                };
            // если встретили повторно — не перетираем true на false
            if (valueOnly)
                entry.valueOnly = true;
            // __till / range-часть пока оставляем как было: один title
            if (part === "till") {
                const tillTitle = (v.valueTitle ?? v.value ?? "").toString().trim();
                if (tillTitle !== "" && !entry.tills.includes(tillTitle)) {
                    entry.tills.push(tillTitle);
                }
                map.set(baseKey, entry);
                continue;
            }
            // base-часть: тут включаем поддержку массивов
            if (schema.isArray) {
                const values = parseBraceOrCsv((v.value ?? "").toString());
                if (values.length === 0) {
                    map.set(baseKey, entry);
                    continue;
                }
                // titles best-effort: если valueTitle похож на список — пытаемся разложить,
                // иначе fallback к values
                const rawTitles = (v.valueTitle ?? "").toString().trim();
                const splitTitles = rawTitles
                    ? rawTitles.split(",").map(s => s.trim()).filter(Boolean)
                    : [];
                const titles = splitTitles.length === values.length ? splitTitles : values;
                if (schema.chipsArray) {
                    // each: чип на элемент
                    for (let i = 0; i < values.length; i++) {
                        const itemValue = values[i];
                        const title = (titles[i] ?? itemValue).toString().trim();
                        if (title === "")
                            continue;
                        // избегаем дублей (по itemValue, т.к. title может совпадать)
                        const exists = entry.singles.some(x => x.itemValue === itemValue);
                        if (!exists)
                            entry.singles.push({ title, itemValue });
                    }
                }
                else {
                    // summary: один чип
                    const sm = schema.chipsArraySummary ?? "join";
                    let summaryTitle = "";
                    if (sm === "count") {
                        summaryTitle = values.length === 1
                            ? (titles[0] ?? values[0])
                            : Loc.selectedCount.replace("{0}", values.length.toString());
                    }
                    else if (sm === "first") {
                        summaryTitle = values.length === 1
                            ? (titles[0] ?? values[0])
                            : `${titles[0] ?? values[0]} +${values.length - 1}`;
                    }
                    else {
                        // join
                        summaryTitle = titles.join(", ");
                    }
                    summaryTitle = summaryTitle.toString().trim();
                    if (summaryTitle !== "") {
                        // summary всегда один, но пусть будет идемпотентно
                        entry.singles = [{ title: summaryTitle }];
                    }
                }
                map.set(baseKey, entry);
                continue;
            }
            // scalar: как раньше
            const title = (v.valueTitle ?? v.value ?? "").toString().trim();
            if (title !== "" && !entry.singles.some(x => x.title === title && x.itemValue == null)) {
                entry.singles.push({ title });
            }
            map.set(baseKey, entry);
        }
        // если у поля обнаружен __till, все «singles» считаем стартами
        for (const map of groups.values()) {
            for (const a of map.values()) {
                if (a.tills.length > 0 && a.starts.length === 0 && a.singles.length > 0) {
                    a.starts.push(...a.singles.map(x => x.title));
                    a.singles = [];
                }
            }
        }
        const items = [];
        // Важно: порядок групп здесь — в порядке обнаружения в entries (Map сохраняет insertion order)
        for (const map of groups.values()) {
            for (const a of map.values()) {
                const hasRange = a.tills.length > 0 || a.starts.length > 0;
                if (hasRange) {
                    const from = a.starts[0] || "";
                    const to = a.tills[0] || "";
                    if (from && to)
                        items.push({
                            key: a.key,
                            groupKey: a.groupKey,
                            label: a.label,
                            title: `${from} > ${to}`,
                            showFull: true,
                            sourceKey,
                            valueOnly: a.valueOnly
                        });
                    else if (from && !to)
                        items.push({
                            key: a.key,
                            groupKey: a.groupKey,
                            label: a.label,
                            title: `${from} >`,
                            showFull: true, sourceKey,
                            valueOnly: a.valueOnly
                        });
                    else if (!from && to)
                        items.push({
                            key: a.key,
                            groupKey: a.groupKey,
                            label: a.label,
                            title: `> ${to}`,
                            showFull: true,
                            sourceKey,
                            valueOnly: a.valueOnly
                        });
                    continue;
                }
                const vals = a.singles.filter(x => x.title.trim() !== "");
                if (vals.length === 0)
                    continue;
                // each: если есть itemValue — делаем по одному ChipItem на элемент
                if (vals.some(x => x.itemValue != null)) {
                    for (const one of vals) {
                        items.push({
                            key: a.key,
                            groupKey: a.groupKey,
                            label: a.label,
                            title: one.title,
                            itemValue: one.itemValue,
                            sourceKey,
                            valueOnly: a.valueOnly
                        });
                    }
                }
                else {
                    // summary / scalar: один чип
                    items.push({
                        key: a.key,
                        groupKey: a.groupKey,
                        label: a.label,
                        title: vals.map(x => x.title).join(", "),
                        sourceKey,
                        valueOnly: a.valueOnly
                    });
                }
            }
        }
        return items;
    }
    /**
     * Обновить чипсы указанной группы (sourceKey).
     *
     * Важно:
     * - мы НЕ чистим `host` целиком (чтобы не уничтожать заголовки/разметку группы)
     * - обновляем только контейнер группы `.chips__group[data-source="..."]`
     * - заголовок группы скрывается при пустом списке, но не удаляется
     */
    render(entries, sourceKey = "default") {
        const nextList = this.normalize(entries, sourceKey);
        // groupKey -> (id -> ChipItem)
        const nextByGroup = new Map();
        for (const it of nextList) {
            let m = nextByGroup.get(it.groupKey);
            if (!m) {
                m = new Map();
                nextByGroup.set(it.groupKey, m);
            }
            const id = this._chipId(it);
            m.set(id, it);
            this.ensureGroupOrder(it.groupKey);
        }
        // 1) удалить из массива чипсы, которых больше нет
        this.chips = this.chips.filter(c => {
            if (c.sourceKey !== sourceKey)
                return true;
            const m = nextByGroup.get(c.groupKey);
            if (!m)
                return false;
            const id = this._chipId(c);
            return m.has(id);
        });
        // 2) обновить существующие
        this.chips = this.chips.map(c => {
            if (c.sourceKey !== sourceKey)
                return c;
            const m = nextByGroup.get(c.groupKey);
            if (!m)
                return c;
            const id = this._chipId(c);
            return m.get(id) ?? c;
        });
        // 3) добавить новые в конец
        for (const it of nextList) {
            const itId = this._chipId(it);
            const exists = this.chips.some(c => c.sourceKey === sourceKey &&
                c.groupKey === it.groupKey &&
                this._chipId(c) === itId);
            if (!exists)
                this.chips.push(it);
        }
        // 4) перерисовать ВСЕ группы (включая пустые из DOM — чтобы скрылись)
        if (this.isGroupedLayout()) {
            // групповой режим: перерисовать ВСЕ группы (включая пустые)
            for (const g of this.getAllGroupKeys()) {
                this.renderGroup(g);
            }
        }
        else {
            // плоский режим: как раньше, без групповых обёрток
            this.renderFlat();
        }
        // 5) общее состояние
        this.setFilteredState(this.chips.length > 0);
    }
    /** Рендер без групп: wrapper + clear-all прямо в host (АГРЕГИРОВАННО, как раньше) */
    renderFlat() {
        // 🔥 как в старой версии: рисуем ВСЕ источники
        const list = this.chips;
        // чистим host полностью
        while (this.host.firstChild)
            this.host.removeChild(this.host.firstChild);
        if (list.length === 0)
            return;
        const wrapper = document.createElement("div");
        wrapper.className = "chips__wrapper";
        for (const it of list) {
            wrapper.appendChild(this.createChip(it));
        }
        const clearAll = document.createElement("span");
        clearAll.className = "chips__clearall";
        // можно оставить data-source пустым/не ставить вовсе — это "clear all"
        const trash = document.createElement("i");
        trash.className = "bt-trash";
        clearAll.appendChild(trash);
        const pos = (this.host.dataset.deletepos ?? "end").toLowerCase() === "start" ? "start" : "end";
        if (pos === "start") {
            this.host.appendChild(clearAll);
            this.host.appendChild(wrapper);
        }
        else {
            this.host.appendChild(wrapper);
            this.host.appendChild(clearAll);
        }
    }
    /** Все groupKey: из DOM + из groupOrder */
    getAllGroupKeys() {
        const keys = new Set(this.groupOrder);
        this.host
            .querySelectorAll(".chips__group[data-group]")
            .forEach(el => {
            const k = (el.dataset.group ?? "").trim();
            if (k)
                keys.add(k);
        });
        return Array.from(keys);
    }
    /** Вернуть id группы чипсов по элементу внутри группы. */
    getChipsGroupId(ctx) {
        const g = ctx.closest(".chips__group");
        return (g?.dataset.group ?? "").trim() || null; // data-group
    }
    /**
     * Очистить только DOM всех групп (состояние массива остаётся).
     * Заголовки групп не удаляются — только скрываются.
     */
    clear() {
        if (!this.isGroupedLayout()) {
            while (this.host.firstChild)
                this.host.removeChild(this.host.firstChild);
            this.setFilteredState(false);
            return;
        }
        for (const groupKey of this.groupOrder) {
            const group = this.findGroup(groupKey);
            if (!group)
                continue;
            group.hidden = true; // <-- важно
            const wrapper = group.querySelector(".chips__wrapper");
            if (wrapper)
                wrapper.replaceChildren();
            const clearAll = group.querySelector(".chips__clearall");
            if (clearAll)
                clearAll.hidden = true;
            const titleEl = this.getGroupTitleEl(group);
            if (titleEl)
                titleEl.hidden = true;
        }
        this.setFilteredState(false);
    }
    /**
     * Полная очистка: DOM + состояние массива.
     * Заголовки групп не удаляются — только скрываются.
     */
    reset() {
        this.chips = [];
        this.clear();
    }
    /**
     * Удалить один чип по ключу и источнику (и из массива, и из DOM).
     * Если группа опустела — скрываем заголовок и кнопку "clear all".
     */
    removeKey(key, sourceKey = "default") {
        // 1) найти DOM-чип (ключи уникальны, поэтому достаточно key+sourceKey)
        const chipEl = this.host.querySelector(`.chips__item[data-fkey="${key}"][data-source="${sourceKey}"]`);
        if (!chipEl)
            return;
        // плоский режим: просто убрать чип и, если wrapper пустой — убрать clear-all
        if (!this.isGroupedLayout()) {
            this.chips = this.chips.filter(c => !(c.key === key && c.sourceKey === sourceKey));
            chipEl.remove();
            const wrapper = this.host.querySelector(".chips__wrapper");
            if (wrapper && wrapper.children.length === 0) {
                this.host.querySelector(".chips__clearall")?.remove();
                wrapper.remove();
            }
            this.setFilteredState(this.chips.length > 0);
            return;
        }
        const groupKey = chipEl.dataset.group ?? "default";
        // 2) обновить состояние
        this.chips = this.chips.filter(c => !(c.key === key && c.sourceKey === sourceKey));
        // 3) удалить DOM-элемент
        chipEl.remove();
        // 4) если в группе больше нет чипсов — скрыть clear-all и заголовок
        const group = this.findGroup(groupKey);
        const wrapper = group?.querySelector(".chips__wrapper");
        if (group && wrapper && wrapper.children.length === 0) {
            group.hidden = true; // <-- важно
            const clearAll = group.querySelector(".chips__clearall");
            if (clearAll)
                clearAll.hidden = true;
            const titleEl = this.getGroupTitleEl(group);
            if (titleEl)
                titleEl.hidden = true;
        }
        // 5) общее состояние
        this.setFilteredState(this.chips.length > 0);
    }
    // ── group rendering ─────────────────────────────────────
    /** Обеспечить наличие groupKey в порядке групп (для детерминированной разметки) */
    ensureGroupOrder(groupKey) {
        if (!this.groupOrder.includes(groupKey)) {
            this.groupOrder.push(groupKey);
        }
    }
    /** Перерисовать одну UI-группу (groupKey), не трогая другие группы в host */
    renderGroup(groupKey) {
        const group = this.getOrCreateGroup(groupKey);
        const list = this.chips.filter(c => c.groupKey === groupKey);
        // Скрываем/показываем целый блок группы
        group.hidden = list.length === 0;
        // Заголовок: может быть в разметке, либо создаётся если задан data-title
        this.ensureGroupTitle(group);
        const titleEl = this.getGroupTitleEl(group);
        if (titleEl)
            titleEl.hidden = list.length === 0;
        const wrapper = this.getOrCreateWrapper(group);
        wrapper.replaceChildren();
        if (list.length === 0) {
            wrapper.hidden = true;
            const clearAll = group.querySelector(".chips__clearall");
            if (clearAll)
                clearAll.hidden = true;
            return;
        }
        wrapper.hidden = false;
        for (const it of list) {
            wrapper.appendChild(this.createChip(it));
        }
        const clearAll = this.getOrCreateClearAll(group, groupKey);
        const pos = this.getDeletePos(group);
        this.ensureGroupChildrenOrder(group, titleEl, clearAll, wrapper, pos);
        clearAll.hidden = false;
    }
    /** Найти существующий контейнер UI-группы по groupKey */
    findGroup(groupKey) {
        return this.host.querySelector(`.chips__group[data-group="${groupKey}"]`);
    }
    /**
     * Получить или создать контейнер UI-группы.
     * Если группы нет в разметке — создаём и вставляем по порядку groupOrder.
     */
    getOrCreateGroup(groupKey) {
        const existing = this.findGroup(groupKey);
        if (existing)
            return existing;
        const group = document.createElement("div");
        group.className = "chips__group";
        group.dataset.group = groupKey;
        const idx = this.groupOrder.indexOf(groupKey);
        const before = this.findNextGroupElementAfterIndex(idx);
        if (before)
            this.host.insertBefore(group, before);
        else
            this.host.appendChild(group);
        return group;
    }
    /** Найти DOM-элемент следующей группы по order (чтобы вставлять между существующими) */
    findNextGroupElementAfterIndex(idx) {
        for (let i = idx + 1; i < this.groupOrder.length; i++) {
            const key = this.groupOrder[i];
            const el = this.findGroup(key);
            if (el)
                return el;
        }
        return null;
    }
    /** Получить позиционирование clear-all: из data-deletepos группы или host */
    getDeletePos(group) {
        const v = (group.dataset.deletepos ?? this.host.dataset.deletepos ?? "end").toLowerCase();
        return v === "start" ? "start" : "end";
    }
    /**
     * Заголовок группы:
     * - если в разметке есть `[data-role="chips-title"]` — используем его
     * - если нет, но у группы задан `data-title` — создаём элемент заголовка
     * - если нет ни того, ни другого — заголовок не создаём (группа без названия)
     */
    ensureGroupTitle(group) {
        const existed = this.getGroupTitleEl(group);
        if (existed)
            return;
        const title = (group.dataset.title ?? "").trim();
        if (!title)
            return;
        const el = document.createElement("div");
        el.className = "chips__title";
        el.dataset.role = "chips-title";
        el.textContent = title;
        group.insertBefore(el, group.firstChild);
    }
    /** Получить заголовок группы (если есть) */
    getGroupTitleEl(group) {
        return group.querySelector(`[data-role="chips-title"]`);
    }
    /** Получить или создать wrapper в группе */
    getOrCreateWrapper(group) {
        let wrapper = group.querySelector(".chips__wrapper");
        if (!wrapper) {
            wrapper = document.createElement("div");
            wrapper.className = "chips__wrapper";
            group.appendChild(wrapper);
        }
        return wrapper;
    }
    /** Получить или создать clear-all в группе */
    getOrCreateClearAll(group, groupKey) {
        let clearAll = group.querySelector(".chips__clearall");
        if (!clearAll) {
            clearAll = document.createElement("span");
            clearAll.className = "chips__clearall";
            clearAll.dataset.group = groupKey;
            const trash = document.createElement("i");
            trash.className = "bt-trash";
            clearAll.appendChild(trash);
            group.appendChild(clearAll);
        }
        else {
            clearAll.dataset.group = groupKey;
        }
        return clearAll;
    }
    /**
     * Обеспечить порядок дочерних элементов в группе:
     * title (если есть) → (clearAll|wrapper) → (wrapper|clearAll)
     */
    ensureGroupChildrenOrder(group, titleEl, clearAll, wrapper, pos) {
        // Соберём желаемый список
        const desired = [];
        if (titleEl)
            desired.push(titleEl);
        if (pos === "start") {
            desired.push(clearAll, wrapper);
        }
        else {
            desired.push(wrapper, clearAll);
        }
        // Вставим в правильном порядке (операции idempotent)
        for (const el of desired) {
            group.appendChild(el);
        }
    }
    // ── misc helpers ────────────────────────────────────────
    /** Переключить CSS-состояние "фильтр включён" на ближайшем контейнере - только для таблиц нужно */
    setFilteredState(on) {
        const block = this.host.closest(".selected-filters");
        if (!block)
            return;
        block.classList.toggle("im-table-filtered", on);
    }
    /** Разбор ключа: поддерживаем только `__till`, остальное — базовый ключ */
    getBaseKey(key) {
        return key.endsWith("__till")
            ? { baseKey: key.slice(0, -6), part: "till" }
            : { baseKey: key, part: "base" };
    }
    /** Создать DOM-элемент чипса */
    createChip(it) {
        const chip = document.createElement("div");
        chip.className = "chips__item" + (it.showFull ? " chips__item_full" : "");
        chip.dataset.fkey = it.key;
        chip.dataset.source = it.sourceKey;
        chip.dataset.group = it.groupKey;
        if (it.itemValue != null) {
            chip.dataset.item = it.itemValue;
        }
        const span = document.createElement("span");
        const text = it.title === "{on}" ? `${it.label}` :
            it.valueOnly ? `${it.title}` :
                `${it.label} - ${it.title}`;
        span.textContent = text;
        chip.appendChild(span);
        const close = document.createElement("i");
        close.className = "bt-delete";
        chip.appendChild(close);
        return chip;
    }
    /** Есть ли группы в разметке (режим групп) */
    isGroupedLayout() {
        return !!this.host.querySelector(".chips__group[data-group]");
    }
    /** Уникальный id чипса (key + itemValue)  */
    _chipId(it) {
        // itemValue участвует только для each-режима
        return it.itemValue != null ? `${it.key}@@${it.itemValue}` : it.key;
    }
}
FilterChipsManager.instances = new WeakMap();

export { FilterChipsManager };
